/**
 * Room Templates API Endpoints
 * 
 * API client functions for room template operations
 * Date: 15-10-2025 16:00:00
 * 
 * @module api/endpoints/room-templates
 */

import { api } from '../index';
import type { 
  RoomTemplate, 
  CreateRoomTemplateData, 
  UpdateRoomTemplateData, 
  RoomTemplateFilters,
  RoomTemplateResponse 
} from '@shared/types/room-template';

/**
 * Room Templates API client
 */
export const roomTemplatesApi = {
  /**
   * Get all room templates with optional filtering
   * @param filters - Filter options
   * @returns Templates list with pagination
   */
  getTemplates: async (filters?: RoomTemplateFilters): Promise<RoomTemplateResponse> => {
    const response = await api.get('/room-templates', { params: filters });
    return response.data;
  },

  /**
   * Get single room template by ID
   * @param id - Template ID
   * @returns Template data with usage statistics
   */
  getTemplate: async (id: string): Promise<RoomTemplateResponse> => {
    const response = await api.get(`/room-templates/${id}`);
    return response.data;
  },

  /**
   * Create new room template
   * @param data - Template data
   * @returns Created template
   */
  createTemplate: async (data: CreateRoomTemplateData): Promise<RoomTemplateResponse> => {
    const response = await api.post('/room-templates', data);
    return response.data;
  },

  /**
   * Update room template
   * @param id - Template ID
   * @param data - Update data
   * @returns Updated template
   */
  updateTemplate: async (id: string, data: UpdateRoomTemplateData): Promise<RoomTemplateResponse> => {
    const response = await api.patch(`/room-templates/${id}`, data);
    return response.data;
  },

  /**
   * Delete room template
   * @param id - Template ID
   * @returns Success response
   */
  deleteTemplate: async (id: string): Promise<RoomTemplateResponse> => {
    const response = await api.delete(`/room-templates/${id}`);
    return response.data;
  },

  /**
   * Get templates by category
   * @param category - Template category
   * @returns Templates in category
   */
  getTemplatesByCategory: async (category: string): Promise<RoomTemplateResponse> => {
    const response = await api.get(`/room-templates/category/${category}`);
    return response.data;
  }
};
